import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import apiV2 from "../../services/apiV2";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import MainContainer from "../../components/MainContainer";
import useAppDarkMode from "../../hooks/useAppDarkMode";
import DocEditorModal from "./DocEditorModal";
import CategoriesModal from "./CategoriesModal";

const emptyForm = (category = "") => ({
	title: "",
	body: "",
	category,
	isPinned: false,
	status: "published",
	eventDate: "",
	metaErrorCode: "",
	metaExternalUrl: "",
	mentionUserIds: [],
	imageUrl: "",
});

function formatDate(value, long = false) {
	if (!value) return null;
	const d = new Date(String(value).length <= 10 ? `${value}T00:00:00` : value);
	if (Number.isNaN(d.getTime())) return null;
	return long
		? d.toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" })
		: d.toLocaleDateString("pt-BR");
}

function categoryMeta(value, categories) {
	const found = (categories || []).find((c) => c.slug === value);
	if (found) return { label: found.name, color: found.color || "#71717a" };
	if (value === "avisos") return { label: "Servidor / Avisos", color: "#f59e0b" };
	return { label: value || "Categoria", color: "#71717a" };
}

function CategoryBadge({ category, categories }) {
	const meta = categoryMeta(category, categories);
	return (
		<span
			className="text-xs font-medium px-2 py-0.5 rounded-full"
			style={{
				background: `${meta.color}22`,
				color: meta.color,
				border: `1px solid ${meta.color}44`,
			}}
		>
			{meta.label}
		</span>
	);
}

function DocIcon({ size = 16, className = "" }) {
	return (
		<svg
			width={size}
			height={size}
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			viewBox="0 0 24 24"
			className={className}
		>
			<path d="M9 12h6m-6 4h6m2 5H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5.586a1 1 0 0 1 .707.293l5.414 5.414A1 1 0 0 1 19 9.414V19a2 2 0 0 1-2 2z" />
		</svg>
	);
}

export default function CompanyDocumentation() {
	const { user } = useContext(AuthContext);
	const darkMode = useAppDarkMode();
	const isAdmin = user?.profile === "admin" || user?.profile === "master";
	const [isMobile, setIsMobile] = useState(
		typeof window !== "undefined" && window.innerWidth < 768
	);
	const overlayBg = darkMode ? "rgba(0, 0, 0, 0.72)" : "rgba(24, 24, 27, 0.45)";
	const themeVars = darkMode
		? {
				"--app-body-bg": "#09090b",
				"--app-surface": "#18181b",
				"--app-border": "#3f3f46",
				"--app-text": "#fafafa",
				"--app-text-muted": "#a1a1aa",
			}
		: {
				"--app-body-bg": "#fafafa",
				"--app-surface": "#ffffff",
				"--app-border": "#e4e4e7",
				"--app-text": "#18181b",
				"--app-text-muted": "#71717a",
			};
	const [category, setCategory] = useState(null);
	const [q, setQ] = useState("");
	const [qDebounced, setQDebounced] = useState("");
	const [dateFrom, setDateFrom] = useState("");
	const [dateTo, setDateTo] = useState("");
	const [mentionedMe, setMentionedMe] = useState(false);
	const [loading, setLoading] = useState(true);
	const [docs, setDocs] = useState([]);
	const [count, setCount] = useState(0);
	const [stats, setStats] = useState({
		total: 0,
		unread: 0,
		published: 0,
		draft: 0,
		countsByCategory: {},
	});
	const [selected, setSelected] = useState(null);
	const [detailLoading, setDetailLoading] = useState(false);
	const [modal, setModal] = useState(null);
	const [editing, setEditing] = useState(null);
	const [form, setForm] = useState(emptyForm());
	const [users, setUsers] = useState([]);
	const [saving, setSaving] = useState(false);
	const [deleteConfirm, setDeleteConfirm] = useState(null);
	const [uploading, setUploading] = useState(false);
	const [previewImage, setPreviewImage] = useState(null);
	const [categories, setCategories] = useState([]);
	const [manageOpen, setManageOpen] = useState(false);

	const writableCategories = useMemo(
		() => (categories || []).filter((c) => c.isWritable),
		[categories]
	);

	const loadCategories = useCallback(async () => {
		try {
			const { data } = await apiV2.get("/company-documents/categories");
			setCategories(Array.isArray(data?.categories) ? data.categories : []);
		} catch (err) {
			toastError(err);
			setCategories([]);
		}
	}, []);

	useEffect(() => {
		const t = setTimeout(() => setQDebounced(q.trim()), 300);
		return () => clearTimeout(t);
	}, [q]);

	useEffect(() => {
		const onResize = () => setIsMobile(window.innerWidth < 768);
		onResize();
		window.addEventListener("resize", onResize);
		return () => window.removeEventListener("resize", onResize);
	}, []);

	const overlayOpen = Boolean(modal || deleteConfirm || previewImage || manageOpen);
	const mobileReaderOpen = isMobile && Boolean(selected || detailLoading);

	const loadDocs = useCallback(async () => {
		setLoading(true);
		try {
			const { data } = await apiV2.get("/company-documents", {
				params: {
					category: category || undefined,
					q: qDebounced || undefined,
					dateFrom: dateFrom || undefined,
					dateTo: dateTo || undefined,
					mentionedMe: mentionedMe ? "true" : undefined,
					limit: 50,
				},
			});
			setDocs(Array.isArray(data?.documents) ? data.documents : []);
			setCount(Number(data?.count) || 0);
			if (data?.stats) {
				setStats({
					total: Number(data.stats.total) || 0,
					unread: Number(data.stats.unread) || 0,
					published: Number(data.stats.published) || 0,
					draft: Number(data.stats.draft) || 0,
					countsByCategory: data.stats.countsByCategory || {},
				});
			}
		} catch (err) {
			toastError(err);
		} finally {
			setLoading(false);
		}
	}, [category, qDebounced, dateFrom, dateTo, mentionedMe]);

	useEffect(() => {
		loadDocs();
	}, [loadDocs]);

	useEffect(() => {
		loadCategories();
	}, [loadCategories]);

	useEffect(() => {
		if (!isAdmin) return;
		api
			.get("/users")
			.then(({ data }) => {
				const list = Array.isArray(data?.users)
					? data.users
					: Array.isArray(data)
						? data
						: [];
				setUsers(list);
			})
			.catch(() => setUsers([]));
	}, [isAdmin]);

	const categoryCountInStats = useMemo(() => {
		if (!category) return stats.total;
		return Number(stats.countsByCategory?.[category] || 0);
	}, [stats, category]);

	const openDetail = async (doc) => {
		setDetailLoading(true);
		try {
			const { data } = await apiV2.get(`/company-documents/${doc.id}`);
			setSelected(data);
			if (!data.isRead) {
				try {
					await apiV2.post(`/company-documents/${doc.id}/read`);
					setDocs((prev) =>
						prev.map((d) => (d.id === doc.id ? { ...d, isRead: true } : d))
					);
					setSelected((s) => (s ? { ...s, isRead: true } : s));
					setStats((s) => ({
						...s,
						unread: Math.max(0, (s.unread || 0) - 1),
					}));
				} catch {
					/* ignore */
				}
			}
		} catch (err) {
			toastError(err);
		} finally {
			setDetailLoading(false);
		}
	};

	const refreshDoc = async (id) => {
		const { data } = await apiV2.get(`/company-documents/${id}`);
		setEditing(data);
		if (selected?.id === id) setSelected(data);
		return data;
	};

	const uploadImageFile = async (documentId, file) => {
		const fd = new FormData();
		fd.append("file", file);
		await apiV2.post(`/company-documents/${documentId}/attachments`, fd);
	};

	const attachImageUrl = async (documentId, url) => {
		await apiV2.post(`/company-documents/${documentId}/attachments`, {
			type: "image",
			url: url.trim(),
			caption: "",
		});
	};

	const openCreate = () => {
		const writable = (categories || []).filter((c) => c.isWritable);
		if (writable.length === 0) {
			toast.info("Crie uma categoria antes de publicar.");
			setManageOpen(true);
			return;
		}
		const preferred =
			writable.find((c) => c.slug === category) || writable[0];
		setEditing(null);
		setForm(emptyForm(preferred.slug));
		setModal("new");
	};

	const openEditLocal = (doc) => {
		if (doc.visibility !== "company") {
			toast.info("Informativos globais só podem ser editados no Master.");
			return;
		}
		setEditing(doc);
		setForm({
			title: doc.title || "",
			body: doc.body || "",
			category: doc.category || "avisos",
			isPinned: !!doc.isPinned,
			status: doc.status || "published",
			eventDate: doc.eventDate ? String(doc.eventDate).slice(0, 10) : "",
			metaErrorCode: doc.metaErrorCode || "",
			metaExternalUrl: doc.metaExternalUrl || "",
			mentionUserIds: (doc.mentions || []).map((m) => m.userId),
			imageUrl: "",
		});
		setModal("edit");
	};

	const handleUploadOnEdit = async (e) => {
		const file = e.target.files?.[0];
		e.target.value = "";
		if (!file || !editing?.id) return;
		if (!file.type.startsWith("image/")) {
			toast.error("Selecione um arquivo de imagem");
			return;
		}
		setUploading(true);
		try {
			await uploadImageFile(editing.id, file);
			toast.success("Imagem vinculada");
			await refreshDoc(editing.id);
			loadDocs();
		} catch (err) {
			toastError(err);
		} finally {
			setUploading(false);
		}
	};

	const handleAddImageUrl = async (url) => {
		if (!editing?.id || !String(url || "").trim()) return;
		setUploading(true);
		try {
			await attachImageUrl(editing.id, url);
			toast.success("Imagem vinculada");
			await refreshDoc(editing.id);
			loadDocs();
		} catch (err) {
			toastError(err);
		} finally {
			setUploading(false);
		}
	};

	const handleRemoveAttachment = async (attId) => {
		if (!editing?.id) return;
		try {
			await apiV2.delete(`/company-documents/${editing.id}/attachments/${attId}`);
			toast.success("Anexo removido");
			await refreshDoc(editing.id);
			loadDocs();
		} catch (err) {
			toastError(err);
		}
	};

	const handleSave = async (formData, pendingFile) => {
		if (!formData.title.trim()) {
			toast.error("Título obrigatório");
			return;
		}
		setSaving(true);
		try {
			const payload = {
				title: formData.title.trim(),
				body: formData.body,
				category: formData.category,
				isPinned: !!formData.isPinned,
				status: formData.status,
				visibility: "company",
				eventDate: formData.eventDate || null,
				metaErrorCode: (formData.metaErrorCode || "").trim() || null,
				metaExternalUrl: (formData.metaExternalUrl || "").trim() || null,
				mentionUserIds: formData.mentionUserIds || [],
			};
			let docId = editing?.id;
			if (docId) {
				await apiV2.put(`/company-documents/${docId}`, payload);
				toast.success("Informativo atualizado");
			} else {
				const { data } = await apiV2.post("/company-documents", payload);
				docId = data?.id;
				toast.success("Informativo criado");
				if (docId && pendingFile) {
					await uploadImageFile(docId, pendingFile);
				}
				if (docId && (formData.imageUrl || "").trim()) {
					await attachImageUrl(docId, formData.imageUrl);
				}
			}
			setModal(null);
			setSelected(null);
			loadDocs();
		} catch (err) {
			toastError(err);
		} finally {
			setSaving(false);
		}
	};

	const handleDeleteLocal = async (doc) => {
		if (doc.visibility !== "company") {
			toast.info("Informativos globais só podem ser removidos no Master.");
			return;
		}
		try {
			await apiV2.delete(`/company-documents/${doc.id}`);
			toast.success("Removido");
			if (selected?.id === doc.id) setSelected(null);
			setDeleteConfirm(null);
			loadDocs();
		} catch (err) {
			toastError(err);
		}
	};

	const handleCreateCategory = async ({ name, color }) => {
		try {
			await apiV2.post("/company-documents/categories", { name, color });
			toast.success("Categoria criada");
			await loadCategories();
			loadDocs();
		} catch (err) {
			toastError(err);
		}
	};

	const handleUpdateCategory = async (id, payload) => {
		try {
			await apiV2.put(`/company-documents/categories/${id}`, payload);
			toast.success("Categoria atualizada");
			await loadCategories();
			loadDocs();
		} catch (err) {
			toastError(err);
		}
	};

	const handleDeleteCategory = async (cat) => {
		if (!window.confirm(`Excluir a categoria "${cat.name}"?`)) return;
		try {
			await apiV2.delete(`/company-documents/categories/${cat.id}`);
			toast.success("Categoria removida");
			if (category === cat.slug) {
				setCategory(null);
				setSelected(null);
			}
			await loadCategories();
			loadDocs();
		} catch (err) {
			toastError(err);
		}
	};

	const statCards = [
		{ label: "Total", value: stats.total, color: "#6366f1" },
		{ label: "Não lidos", value: stats.unread, color: "#f59e0b" },
		{ label: "Nesta categoria", value: categoryCountInStats, color: "#22c55e" },
		{ label: "Resultado filtro", value: count, color: "#06b6d4" },
	];

	const pageTitle = i18n.t("mainDrawer.listItems.documentation") || "Informativos";

	const detailContent = selected ? (
		<>
			<div className="flex flex-col sm:flex-row sm:items-start justify-between p-4 md:p-6 lg:p-8 border-b border-[var(--app-border)] gap-3 shrink-0">
				<div className="min-w-0 flex-1">
					<div className="flex items-center gap-2 mb-2 flex-wrap">
						<CategoryBadge category={selected.category} categories={categories} />
						{selected.isPinned ? (
							<span className="text-xs text-[var(--app-text-muted)]">Fixado</span>
						) : null}
						{selected.isRead === false ? (
							<span className="text-xs px-2 py-0.5 rounded-full bg-indigo-500/15 text-indigo-500">
								Novo
							</span>
						) : null}
					</div>
					<h2 className="text-xl md:text-2xl lg:text-3xl font-bold m-0 text-[var(--app-text)] leading-tight">
						{selected.title}
					</h2>
					<p className="text-xs mt-1 mb-0 text-[var(--app-text-muted)]">
						{selected.createdByUserName
							? `Criado por ${selected.createdByUserName}`
							: ""}
						{selected.createdByUserName &&
						formatDate(selected.eventDate || selected.createdAt, true)
							? " · "
							: ""}
						{formatDate(selected.eventDate || selected.createdAt, true) || ""}
						{selected.metaErrorCode ? ` · Meta: ${selected.metaErrorCode}` : ""}
					</p>
				</div>
				{isAdmin && selected.visibility === "company" ? (
					<div className="flex items-center gap-2 shrink-0">
						<button
							type="button"
							onClick={() => openEditLocal(selected)}
							className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium border border-[var(--app-border)] bg-[var(--app-body-bg)] text-[var(--app-text)] cursor-pointer"
						>
							Editar
						</button>
						<button
							type="button"
							onClick={() => setDeleteConfirm(selected)}
							className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium cursor-pointer border border-red-500/30 bg-red-500/10 text-red-500"
						>
							Excluir
						</button>
					</div>
				) : null}
			</div>
			<div className="p-4 md:p-6 lg:p-8 flex-1 overflow-y-auto min-h-0">
				{selected.metaExternalUrl ? (
					<p className="text-sm mb-4">
						<a
							href={selected.metaExternalUrl}
							target="_blank"
							rel="noreferrer"
							className="text-indigo-500"
						>
							Link externo Meta
						</a>
					</p>
				) : null}
				<p className="text-sm md:text-base leading-relaxed m-0 whitespace-pre-wrap text-[var(--app-text)] max-w-3xl">
					{selected.body}
				</p>
				{(selected.attachments || []).length > 0 ? (
					<div className="mt-6">
						<h3 className="text-sm font-semibold m-0 mb-2">Anexos</h3>
						<div className="flex flex-wrap gap-2">
							{(selected.attachments || []).map((att) =>
								att.type === "image" ? (
									<button
										key={att.id}
										type="button"
										onClick={() =>
											setPreviewImage({
												url: att.url,
												caption: att.caption || "Anexo",
											})
										}
										className="h-14 w-14 md:h-20 md:w-20 rounded-lg overflow-hidden border border-[var(--app-border)] bg-[var(--app-body-bg)] p-0 cursor-pointer"
										title="Clique para ampliar"
									>
										<img
											src={att.url}
											alt={att.caption || "anexo"}
											className="h-full w-full object-cover"
										/>
									</button>
								) : (
									<a
										key={att.id}
										href={att.url}
										target="_blank"
										rel="noreferrer"
										className="inline-flex items-center h-14 px-3 rounded-lg border border-[var(--app-border)] bg-[var(--app-body-bg)] text-indigo-500 text-xs truncate max-w-[10rem]"
									>
										{att.caption || "Link"}
									</a>
								)
							)}
						</div>
					</div>
				) : null}
				{(selected.mentions || []).length > 0 ? (
					<p className="text-xs mt-6 mb-0 text-[var(--app-text-muted)]">
						Mencionados:{" "}
						{(selected.mentions || [])
							.map((m) => m.userName || `#${m.userId}`)
							.join(", ")}
					</p>
				) : null}
			</div>
		</>
	) : null;

	return (
		<MainContainer>
			<div
				className={`flex flex-col h-full min-h-0 bg-[var(--app-body-bg)] text-[var(--app-text)]${
					overlayOpen || mobileReaderOpen ? " overflow-hidden pointer-events-none" : ""
				}`}
				aria-hidden={overlayOpen || mobileReaderOpen || undefined}
			>
				{/* Header */}
				<header className="flex items-center justify-between gap-2 px-3 md:px-6 py-3 md:py-4 border-b border-[var(--app-border)] bg-[var(--app-surface)] shrink-0">
					<div className="flex items-center gap-2 md:gap-3 min-w-0">
						<div className="w-8 h-8 md:w-10 md:h-10 rounded-lg flex items-center justify-center bg-indigo-500 text-white shrink-0">
							<DocIcon size={isMobile ? 16 : 18} />
						</div>
						<h1 className="text-base md:text-xl font-semibold tracking-tight m-0 truncate">
							{pageTitle}
						</h1>
					</div>
					{isAdmin ? (
						<button
							type="button"
							onClick={openCreate}
							className="flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-2 rounded-lg text-sm font-medium text-white bg-indigo-500 hover:opacity-90 active:scale-95 transition-all border-0 cursor-pointer shrink-0"
						>
							<svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
								<path d="M12 5v14M5 12h14" />
							</svg>
							<span className="md:hidden">Novo</span>
							<span className="hidden md:inline">Novo informativo</span>
						</button>
					) : null}
				</header>

				{/* Mobile: tudo empilha e rola. Desktop: stats/categorias fixos no topo, lista + leitor lado a lado. */}
				<div className="flex-1 min-h-0 overflow-y-auto md:overflow-hidden px-3 md:px-6 pb-4 md:pb-5 flex flex-col gap-3 md:gap-3">
					{/* Stats */}
					<div className="grid grid-cols-2 md:grid-cols-4 gap-2 shrink-0">
						{statCards.map((s) => (
							<div
								key={s.label}
								className="flex items-center justify-between gap-2 rounded-xl px-3 py-2 md:px-3.5 md:py-2.5 bg-[var(--app-surface)] border border-[var(--app-border)]"
							>
								<span className="text-[11px] uppercase tracking-wide font-medium text-[var(--app-text-muted)] truncate">
									{s.label}
								</span>
								<span className="text-base font-bold tabular-nums leading-none" style={{ color: s.color }}>
									{s.value}
								</span>
							</div>
						))}
					</div>

					{/* Categorias */}
					<section className="rounded-xl bg-[var(--app-surface)] border border-[var(--app-border)] px-3 py-2.5 shrink-0">
						<div className="flex flex-wrap items-center gap-2">
							<button
								type="button"
								onClick={() => {
									setCategory(null);
									setSelected(null);
								}}
								className={`px-2.5 py-1.5 rounded-lg text-[13px] font-medium border-0 cursor-pointer ${
									category === null
										? "bg-indigo-500/15 text-indigo-500"
										: "bg-[var(--app-body-bg)] text-[var(--app-text-muted)]"
								}`}
							>
								Todos
								<span className="ml-1 text-xs opacity-80">({stats.total})</span>
							</button>
							{categories.map((cat) => {
								const active = category === cat.slug;
								return (
									<button
										key={cat.slug}
										type="button"
										onClick={() => {
											setCategory(cat.slug);
											setSelected(null);
										}}
										className="px-2.5 py-1.5 rounded-lg text-[13px] font-medium border-0 cursor-pointer inline-flex items-center gap-1.5"
										style={
											active
												? { background: `${cat.color}18`, color: cat.color }
												: {
														background: "var(--app-body-bg)",
														color: "var(--app-text-muted)",
												  }
										}
									>
										<span
											className="w-2 h-2 rounded-full shrink-0"
											style={{ background: cat.color }}
										/>
										{cat.name}
										{cat.isSystem ? (
											<span className="text-[10px] opacity-70">fixa</span>
										) : null}
										<span className="text-xs opacity-80">
											({Number(stats.countsByCategory?.[cat.slug] || 0)})
										</span>
									</button>
								);
							})}
							{isAdmin ? (
								<button
									type="button"
									onClick={() => setManageOpen(true)}
									className="px-2.5 py-1.5 rounded-lg text-[13px] font-medium border border-dashed border-[var(--app-border)] bg-transparent text-[var(--app-text-muted)] cursor-pointer"
								>
									+ Categoria
								</button>
							) : null}
						</div>
					</section>

					{/* Lista + leitor */}
					<div className="flex flex-col gap-3 md:flex-row md:flex-1 md:min-h-0 md:gap-4">
						{/* Coluna esquerda: filtros + lista */}
						<div className="flex flex-col gap-3 md:w-[380px] lg:w-[420px] md:shrink-0 md:min-h-0 md:overflow-hidden">
							{/* Busca / filtros */}
							<section className="rounded-2xl bg-[var(--app-surface)] border border-[var(--app-border)] p-3 md:p-4 shrink-0">
								<div className="relative mb-3">
									<svg
										className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--app-text-muted)] pointer-events-none"
										width="14"
										height="14"
										fill="none"
										stroke="currentColor"
										strokeWidth="2"
										viewBox="0 0 24 24"
									>
										<circle cx="11" cy="11" r="8" />
										<path d="M21 21l-4.35-4.35" />
									</svg>
									<input
										value={q}
										onChange={(e) => setQ(e.target.value)}
										placeholder="Buscar título, conteúdo..."
										className="w-full pl-9 pr-3 py-2 rounded-xl text-sm outline-none bg-[var(--app-body-bg)] text-[var(--app-text)] border border-[var(--app-border)]"
									/>
								</div>
								<div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
									<div className="min-w-0">
										<label className="text-xs mb-1 block text-[var(--app-text-muted)]">De</label>
										<input
											type="date"
											value={dateFrom}
											onChange={(e) => setDateFrom(e.target.value)}
											className="w-full min-w-0 px-2 py-1.5 rounded-lg text-xs outline-none bg-[var(--app-body-bg)] text-[var(--app-text)] border border-[var(--app-border)]"
										/>
									</div>
									<div className="min-w-0">
										<label className="text-xs mb-1 block text-[var(--app-text-muted)]">Até</label>
										<input
											type="date"
											value={dateTo}
											onChange={(e) => setDateTo(e.target.value)}
											className="w-full min-w-0 px-2 py-1.5 rounded-lg text-xs outline-none bg-[var(--app-body-bg)] text-[var(--app-text)] border border-[var(--app-border)]"
										/>
									</div>
								</div>
								<button
									type="button"
									onClick={() => {
										setMentionedMe((v) => !v);
										setSelected(null);
									}}
									className={`w-full px-3 py-2 rounded-xl text-xs font-semibold border cursor-pointer transition-all ${
										mentionedMe
											? "bg-indigo-500/15 text-indigo-500 border-indigo-500/40"
											: "bg-[var(--app-body-bg)] text-[var(--app-text-muted)] border-[var(--app-border)]"
									}`}
								>
									{mentionedMe ? "Mostrando: marcados em mim" : "Filtrar: marcados em mim"}
								</button>
							</section>

							{/* Lista */}
							<section className="rounded-2xl bg-[var(--app-surface)] border border-[var(--app-border)] p-3 md:p-4 md:flex-1 md:min-h-0 md:flex md:flex-col">
								<p className="text-xs m-0 mb-3 text-[var(--app-text-muted)] shrink-0">
									{loading
										? "Carregando..."
										: `${count} informativo${count !== 1 ? "s" : ""}`}
								</p>
								{!loading && docs.length === 0 ? (
									<div className="flex flex-col items-center justify-center gap-2 py-10 text-[var(--app-text-muted)] md:flex-1">
										<DocIcon size={32} />
										<p className="text-sm m-0">Nenhum informativo</p>
									</div>
								) : (
									<div className="flex flex-col gap-2 md:flex-1 md:min-h-0 md:overflow-y-auto">
										{docs.map((doc) => {
											const active = selected?.id === doc.id;
											return (
												<button
													key={doc.id}
													type="button"
													onClick={() => openDetail(doc)}
													className="w-full text-left p-3 rounded-xl transition-all cursor-pointer border"
													style={
														active
															? {
																	background: "rgba(99,102,241,0.12)",
																	borderColor: "#6366f1",
															  }
															: {
																	background: "var(--app-body-bg)",
																	borderColor: "transparent",
															  }
													}
												>
													<div className="flex items-start justify-between gap-2 mb-1">
														<span className="text-sm font-medium leading-snug text-[var(--app-text)]">
															{doc.title}
														</span>
														{doc.isRead === false ? (
															<span className="w-2 h-2 rounded-full shrink-0 mt-1 bg-indigo-500" />
														) : null}
													</div>
													<div className="flex items-center gap-2 flex-wrap mt-1.5">
														<CategoryBadge category={doc.category} categories={categories} />
														{doc.isPinned ? (
															<span className="text-xs text-[var(--app-text-muted)]">Fixado</span>
														) : null}
														{(doc.mentions || []).some(
															(m) => Number(m.userId) === Number(user?.id)
														) ? (
															<span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-600 font-medium">
																Você
															</span>
														) : null}
													</div>
													<p className="text-xs mt-1 mb-0 text-[var(--app-text-muted)]">
														{doc.createdByUserName
															? `Por ${doc.createdByUserName}`
															: doc.visibility === "global"
																? "Global"
																: "Empresa"}
														{formatDate(doc.eventDate || doc.createdAt)
															? ` · ${formatDate(doc.eventDate || doc.createdAt)}`
															: ""}
													</p>
												</button>
											);
										})}
									</div>
								)}
							</section>
						</div>

						{/* Leitor — desktop ao lado da lista */}
						<section className="hidden md:flex rounded-2xl bg-[var(--app-surface)] border border-[var(--app-border)] flex-1 min-h-0 flex-col overflow-hidden">
							{detailLoading ? (
								<div className="flex-1 flex items-center justify-center text-[var(--app-text-muted)] text-sm py-10">
									Carregando...
								</div>
							) : selected ? (
								detailContent
							) : (
								<div className="flex flex-col items-center justify-center gap-3 text-[var(--app-text-muted)] flex-1 py-12 px-6">
									<DocIcon size={40} />
									<p className="m-0 text-center text-sm">Selecione um informativo à esquerda para ler</p>
								</div>
							)}
						</section>
					</div>
				</div>
			</div>

			{/* Leitor fullscreen no celular */}
			{mobileReaderOpen
				? createPortal(
						<div
							className="tailwick-app fixed inset-0 z-[1250] flex flex-col bg-[var(--app-surface)] text-[var(--app-text)]"
							data-theme={darkMode ? "dark" : "light"}
							style={themeVars}
							role="dialog"
							aria-modal="true"
						>
							<div className="flex items-center gap-2 px-3 py-3 border-b border-[var(--app-border)] shrink-0 bg-[var(--app-surface)] safe-top">
								<button
									type="button"
									onClick={() => setSelected(null)}
									className="inline-flex items-center justify-center w-10 h-10 rounded-xl border-0 bg-[var(--app-body-bg)] text-[var(--app-text)] cursor-pointer text-lg"
									aria-label="Voltar"
								>
									←
								</button>
								<span className="text-sm font-semibold truncate flex-1">Informativo</span>
							</div>
							<div className="flex-1 min-h-0 overflow-y-auto flex flex-col bg-[var(--app-surface)]">
								{detailLoading ? (
									<div className="flex-1 flex items-center justify-center text-[var(--app-text-muted)] text-sm py-10">
										Carregando...
									</div>
								) : (
									detailContent
								)}
							</div>
						</div>,
						document.body
				  )
				: null}

			{/* Preview imagem ampliada */}
			{previewImage
				? createPortal(
						<div
							className="tailwick-app fixed inset-0 z-[1400] flex items-center justify-center p-4 overflow-hidden"
							data-theme={darkMode ? "dark" : "light"}
							style={{ background: darkMode ? "rgba(0,0,0,0.85)" : "rgba(24,24,27,0.65)" }}
							onClick={() => setPreviewImage(null)}
							onWheel={(e) => e.preventDefault()}
							role="presentation"
						>
							<div
								className="relative max-w-[90vw] max-h-[90vh] flex flex-col gap-3"
								onClick={(e) => e.stopPropagation()}
								role="dialog"
							>
								<button
									type="button"
									onClick={() => setPreviewImage(null)}
									className="absolute -top-10 right-0 text-white text-sm border-0 bg-transparent cursor-pointer"
								>
									Fechar ✕
								</button>
								<img
									src={previewImage.url}
									alt={previewImage.caption}
									className="max-w-[90vw] max-h-[85vh] rounded-xl object-contain shadow-2xl"
								/>
							</div>
						</div>,
						document.body
				  )
				: null}

			{modal ? (
				<DocEditorModal
					mode={modal}
					initialForm={form}
					editing={editing}
					users={users}
					categories={
						form.category &&
						form.category !== "avisos" &&
						!writableCategories.some((c) => c.slug === form.category)
							? [
									...writableCategories,
									{
										slug: form.category,
										name: categoryMeta(form.category, categories).label,
										color: categoryMeta(form.category, categories).color,
										isWritable: true,
									},
							  ]
							: writableCategories
					}
					saving={saving}
					uploading={uploading}
					onClose={() => setModal(null)}
					onSave={handleSave}
					onUploadImage={handleUploadOnEdit}
					onAddImageUrl={handleAddImageUrl}
					onRemoveAttachment={handleRemoveAttachment}
				/>
			) : null}

			{manageOpen ? (
				<CategoriesModal
					categories={categories}
					saving={saving}
					onClose={() => setManageOpen(false)}
					onCreate={handleCreateCategory}
					onUpdate={handleUpdateCategory}
					onDelete={handleDeleteCategory}
					themeVars={themeVars}
					overlayBg={overlayBg}
				/>
			) : null}

			{/* Delete confirm */}
			{deleteConfirm
				? createPortal(
						<div
							className="tailwick-app fixed inset-0 z-[1300] flex items-center justify-center p-4 overflow-hidden"
							data-theme={darkMode ? "dark" : "light"}
							style={{ ...themeVars, background: overlayBg, color: "var(--app-text)" }}
							onClick={() => setDeleteConfirm(null)}
							onWheel={(e) => e.preventDefault()}
							role="presentation"
						>
							<div
								className="w-full max-w-sm rounded-2xl p-6 flex flex-col gap-4 bg-[var(--app-surface)] border border-[var(--app-border)] text-[var(--app-text)]"
								onClick={(e) => e.stopPropagation()}
								role="dialog"
							>
								<h3 className="text-lg font-semibold m-0 text-[var(--app-text)]">
									Excluir informativo?
								</h3>
								<p className="text-sm m-0 text-[var(--app-text-muted)]">
									Esta ação não pode ser desfeita.
								</p>
								<div className="flex gap-2 justify-end">
									<button
										type="button"
										onClick={() => setDeleteConfirm(null)}
										className="px-4 py-2 rounded-xl text-sm font-medium border-0 bg-transparent cursor-pointer text-[var(--app-text-muted)]"
									>
										Cancelar
									</button>
									<button
										type="button"
										onClick={() => handleDeleteLocal(deleteConfirm)}
										className="px-5 py-2 rounded-xl text-sm font-semibold text-white bg-red-500 border-0 cursor-pointer"
									>
										Excluir
									</button>
								</div>
							</div>
						</div>,
						document.body
				  )
				: null}
		</MainContainer>
	);
}
